import { type FormEvent, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../api/client';
import type { Invitation, Organization, Role } from '@dfx-transfer/shared-types';
import { RoleGuard } from '../components/RoleGuard';
import { useTransferUser } from '../auth/useTransferUser';

export function InvitationsPage() {
  return (
    <RoleGuard allow={['dfx_super_admin', 'org_admin']}>
      <InvitationsPageInner />
    </RoleGuard>
  );
}

function InvitationsPageInner() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { user } = useTransferUser();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('org_uploader');
  const [error, setError] = useState<string | null>(null);

  const orgs = useQuery({
    enabled: user?.role === 'dfx_super_admin',
    queryKey: ['orgs'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<Organization[]>('/api/v1/orgs', { token });
    },
  });

  // Default to the user's own org for org_admin; first org for super-admin.
  useEffect(() => {
    if (orgId) return;
    if (user?.role === 'org_admin' && user.org_id) setOrgId(user.org_id);
    else if (orgs.data && orgs.data[0]) setOrgId(orgs.data[0].id);
  }, [orgs.data, user, orgId]);

  const invites = useQuery({
    enabled: !!orgId,
    queryKey: ['invitations', orgId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return apiRequest<Invitation[]>(`/api/v1/orgs/${orgId}/invitations`, { token });
    },
  });

  const createInvite = useMutation({
    mutationFn: async (input: { email: string; role: Role }) => {
      const token = await getAccessTokenSilently();
      return apiRequest<Invitation>(`/api/v1/orgs/${orgId}/invitations`, {
        token,
        method: 'POST',
        body: input,
      });
    },
    onSuccess: () => {
      setEmail('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['invitations', orgId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || !orgId) return;
    createInvite.mutate({ email, role });
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold">Invitations</h1>
        <p className="mt-1 text-[color:var(--color-text-secondary)]">
          Invite licensor users by email. They'll receive a SendGrid email with a one-time link.
        </p>
      </header>

      {user?.role === 'dfx_super_admin' && (
        <label className="block max-w-sm">
          <span className="text-sm text-[color:var(--color-text-secondary)]">Organization</span>
          <select
            value={orgId ?? ''}
            onChange={(e) => setOrgId(e.target.value || null)}
            className="mt-1 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] px-3 py-2"
          >
            {(orgs.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <section className="rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-6">
        <h2 className="mb-4 text-lg font-medium">Send invitation</h2>
        <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-3">
          <label className="block sm:col-span-2">
            <span className="text-sm text-[color:var(--color-text-secondary)]">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="mt-1 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] px-3 py-2"
              required
            />
          </label>
          <label className="block">
            <span className="text-sm text-[color:var(--color-text-secondary)]">Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="mt-1 w-full rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] px-3 py-2"
            >
              <option value="org_uploader">Uploader</option>
              <option value="org_admin">Admin</option>
            </select>
          </label>
          {error && (
            <div className="sm:col-span-3 rounded border border-[color:var(--color-error-muted)] p-3 text-sm text-[color:var(--color-error)]">
              {error}
            </div>
          )}
          <div className="sm:col-span-3">
            <button
              type="submit"
              disabled={!orgId || createInvite.isPending}
              className="rounded bg-[color:var(--color-crimson)] px-5 py-2 font-medium text-white disabled:opacity-50"
            >
              {createInvite.isPending ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </form>
      </section>

      <section>
        <h2 className="mb-4 text-lg font-medium">Pending and recent</h2>
        {invites.isLoading ? (
          <div>Loading…</div>
        ) : invites.data?.length ? (
          <ul className="divide-y divide-[color:var(--color-border)] rounded border border-[color:var(--color-border)]">
            {invites.data.map((i) => (
              <li key={i.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <div className="font-medium">{i.email}</div>
                  <div className="font-mono text-xs text-[color:var(--color-text-muted)]">
                    {i.role} · expires {new Date(i.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="font-mono text-xs">
                  {i.accepted_at ? (
                    <span className="text-[color:var(--color-success)]">accepted</span>
                  ) : (
                    <span className="text-[color:var(--color-warning)]">pending</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-[color:var(--color-text-secondary)]">No invitations yet.</div>
        )}
      </section>
    </div>
  );
}
